import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Branch, BranchContact } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import BranchContactsSection from './BranchContactsSection';

interface BranchFormProps {
  branch?: Branch;
  onSubmit: (data: Partial<Branch>) => void;
  onCancel: () => void;
}

const buildInitialContacts = (branch?: Branch): BranchContact[] => {
  if (branch?.contacts && Array.isArray(branch.contacts) && branch.contacts.length > 0) {
    return branch.contacts.map((c) => ({
      manager: c.manager || '',
      phone: c.phone || '',
      email: c.email || '',
    }));
  }
  // Fall back to legacy single fields (vessel legacy has two managers)
  if (branch) {
    const rows: BranchContact[] = [];
    const firstManager = branch.type === 'vessel' ? branch.vesselManager1 : branch.manager;
    if (firstManager || branch.phone || branch.email) {
      rows.push({ manager: firstManager || '', phone: branch.phone || '', email: branch.email || '' });
    }
    if (branch.type === 'vessel' && branch.vesselManager2) {
      rows.push({ manager: branch.vesselManager2, phone: '', email: '' });
    }
    if (rows.length > 0) return rows;
  }
  return [{ manager: '', phone: '', email: '' }];
};

const BranchForm: React.FC<BranchFormProps> = ({ branch, onSubmit, onCancel }) => {
  // Fetch employees from API - NO MOCK DATA
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<Branch>({
    defaultValues: {
      name: branch?.name || '',
      type: branch?.type || 'office',
      location: branch?.location || '',
      amount: branch?.amount ?? 0,
      status: branch?.status || 'active',
      callSign: branch?.callSign || '',
      imoNumber: branch?.imoNumber || '',
      flag: branch?.flag || '',
      classificationSociety: branch?.classificationSociety || '',
      contacts: buildInitialContacts(branch),
    }
  });

  const watchedType = watch('type');
  const isVesselType = watchedType === 'vessel';

  const handleFormSubmit = (data: Branch) => {
    // Drop contact rows where every field is empty
    const contacts = (data.contacts || [])
      .map((c) => ({
        manager: c.manager || '',
        phone: c.phone?.trim() || '',
        email: c.email?.trim() || '',
      }))
      .filter((c) => c.manager || c.phone || c.email);

    const cleaned: any = { ...data, contacts };

    // Keep legacy single-value fields in sync (Strapi rejects "" on some types - use null)
    cleaned.manager = contacts[0]?.manager || null;
    cleaned.vesselManager1 = data.type === 'vessel' ? (contacts[0]?.manager || null) : null;
    cleaned.vesselManager2 = data.type === 'vessel' ? (contacts[1]?.manager || null) : null;
    cleaned.phone = contacts.find((c) => c.phone)?.phone || null;
    cleaned.email = contacts.find((c) => c.email)?.email || null;
    if (cleaned.location === '') cleaned.location = null;
    if (cleaned.amount === '' || cleaned.amount === undefined || cleaned.amount === null) {
      cleaned.amount = 0;
    }

    onSubmit({
      ...cleaned,
      ...(branch ? { id: branch.id, documentId: branch.documentId } : {}),
      createdAt: branch?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  };

  const typeOptions = [
    { value: 'office', label: 'Office' },
    { value: 'vessel', label: 'Vessel' },
    { value: 'other', label: 'Other' }
  ];

  // Employee options from API - NO MOCK DATA
  const employeeOptions = useMemo(() => {
    const options = (employees || [])
      .filter((emp: any) => emp.status === 'active' || emp.status === 'Active')
      .map((emp: any) => ({
        value: emp.documentId || emp.id?.toString() || '',
        label: `${emp.name || ''} - ${emp.rank || emp.position || 'Employee'}`
      }))
      .filter((opt: any) => opt.value);
    return [{ value: '', label: 'Select manager (optional)' }, ...options];
  }, [employees]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {branch ? 'Edit Vessel/Headquarters' : 'Add New Vessel/Headquarters'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Vessel/Headquarters Name"
            {...register('name', { 
              required: 'Vessel/Headquarters name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' }
            })}
            error={errors.name?.message}
            placeholder="Enter vessel or headquarters name"
          />

          <Select
            label="Type"
            {...register('type', { required: 'Type is required' })}
            options={typeOptions}
            error={errors.type?.message}
          />
        </div>

        <Input
          label="Location"
          {...register('location')}
          error={errors.location?.message}
          placeholder="City, State/Country"
        />

        <BranchContactsSection
          control={control}
          register={register}
          errors={errors}
          employeeOptions={employeeOptions}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Amount"
            type="number"
            step="0.01"
            {...register('amount', {
              min: { value: 0, message: 'Amount cannot be negative' }
            })}
            error={errors.amount?.message}
            placeholder="0.00"
          />

          <Select
            label="Status"
            {...register('status', { required: 'Status is required' })}
            options={statusOptions}
            error={errors.status?.message}
          />
        </div>

        {/* Vessel-specific fields */}
        {isVesselType && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Vessel Configuration
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    You can optionally assign vessel managers and fill in the vessel-specific information below.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Call Sign"
                {...register('callSign')}
                error={errors.callSign?.message}
                placeholder="Enter vessel call sign"
              />

              <Input
                label="IMO Number"
                type="number"
                {...register('imoNumber', {
                  pattern: {
                    value: /^[0-9]*$/,
                    message: 'IMO Number must contain only numbers'
                  }
                })}
                error={errors.imoNumber?.message}
                placeholder="Enter IMO number"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Flag"
                {...register('flag')}
                error={errors.flag?.message}
                placeholder="Enter vessel flag (e.g., Panama, Liberia)"
              />

              <Input
                label="Classification Society"
                {...register('classificationSociety')}
                error={errors.classificationSociety?.message}
                placeholder="Enter classification society (e.g., DNV, Lloyd's)"
              />
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {branch ? 'Update Vessel/Headquarters' : 'Create Vessel/Headquarters'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BranchForm;